import { Component } from 'react'
import * as Sentry from '@sentry/react'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'
import { getCurrentPageContext, getCurrentTenantFromPath, getRuntimeEnv } from '../utils/sentryConfig'

/**
 * Error Boundary Component
 *
 * Production: silently redirects to home screen without showing any error UI.
 * Development: shows detailed error info for debugging.
 *
 * Errors are always logged to console and reported to Sentry before any action.
 */
class ErrorBoundary extends Component {
  state = { hasError: false, error: null, errorInfo: null }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log for monitoring and debugging (AC5)
    logger.error('Error caught by Error Boundary:', sanitizeLogMessage(error), sanitizeLogMessage(errorInfo))

    // Report to Sentry before redirecting (AC5)
    const pageContext = getCurrentPageContext()
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
      tags: {
        tenant_url: pageContext.tenantUrl,
        current_path: pageContext.currentPath,
      },
      extra: {
        pageUrl: pageContext.pageUrl,
      },
    })

    // In production, seamlessly redirect to home without showing any error UI (AC1, AC2, AC3)
    // Use replace so the broken page is not in browser history
    if (getRuntimeEnv('REACT_APP_ENV', 'development') !== 'development') {
      const tenantUrl = getCurrentTenantFromPath()
      window.location.replace(tenantUrl === 'unknown' ? '/' : `/${tenantUrl}`)
    } else {
      this.setState({ hasError: true, error, errorInfo })
    }
  }

  render() {
    // Development-only error details — production never reaches this branch
    if (
      this.state.hasError &&
      getRuntimeEnv('REACT_APP_ENV', 'development') === 'development'
    ) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
          <h1 style={{ color: 'red' }}>Error Boundary (dev only)</h1>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error?.toString()}
            {this.state.errorInfo?.componentStack}
          </pre>
          <button onClick={() => window.location.replace('/')}>
            Go to Home
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
